import * as React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '@/auth/AuthProvider';
import { useAgency } from '@/auth/AgencyProvider';
import { describeRpcError, useRpcErrorMessage } from '@/lib/rpcErrors';

/**
 * The house data pattern, once. Lifted from useInvoices (RpcError, asRpcError, num) plus the two hooks
 * every fleet screen needs: a live read keyed to the current agency, and a serialized mutation whose
 * error arrives translated.
 */

/** A PostgREST error narrowed to what the page needs to translate it: the SQLSTATE and the message. */
export interface RpcError {
  /** Postgres SQLSTATE, e.g. '42501' (insufficient_privilege), '55000' (wrong state). */
  code: string | undefined;
  message: string;
}

export type ActionResult<T = void> = { data: T | null; error: RpcError | null };

/** Normalize a Supabase/PostgREST error object to the shape the page translates. */
export function asRpcError(error: { code?: string; message?: string } | null | undefined): RpcError | null {
  if (!error) return null;
  return { code: error.code, message: error.message ?? 'Unknown error' };
}

/** PostgREST delivers `numeric` as a string; null and undefined become 0. */
export function num(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0;
  return typeof v === 'string' ? Number(v) : v;
}

/** Like `num`, but keeps null (a column with no value is not a zero). */
export function numOrNull(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isNaN(n) ? null : n;
}

export type LiveState<T> =
  | { status: 'loading'; data?: undefined; refreshing: false; reload: () => void }
  | { status: 'error'; data?: undefined; refreshing: false; reload: () => void }
  | { status: 'ready'; data: T; refreshing: boolean; reload: () => void };

export type LiveFetch<T> = (
  client: SupabaseClient,
  agencyId: string,
) => Promise<{ data: T } | { error: RpcError }>;

type Inner<T> = { status: 'loading' | 'error' } | { status: 'ready'; data: T; refreshing: boolean };

function sameKey(a: readonly unknown[] | null, b: readonly unknown[]): boolean {
  return a !== null && a.length === b.length && a.every((v, i) => Object.is(v, b[i]));
}

/**
 * A read keyed to the current agency. `fetch` runs whenever the agency, a dep or the reload nonce
 * changes; it decides what is fatal (return `{ error }`: only the primary read) and what degrades
 * (a secondary read logs a console.warn and returns partial data). Before the agency is ready it stays
 * `loading`; an `active` flag drops the answer of a superseded run.
 *
 * A `reload()` of the same query (same client, agency and deps) keeps the last ready data on screen
 * with `refreshing: true` until the new answer lands, so a save does not blank the table or unmount an
 * open sheet. A new query (the agency or a dep changed) starts again from `loading`: rows of one
 * filter never stand in for another. A failed reload drops to `error`.
 */
export function useLiveQuery<T>(fetch: LiveFetch<T>, deps: React.DependencyList): LiveState<T> {
  const { client } = useAuth();
  const agency = useAgency();
  const agencyId = agency.status === 'ready' ? agency.agency.id : null;
  const [nonce, setNonce] = React.useState(0);
  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  const [state, setState] = React.useState<Inner<T>>({ status: 'loading' });

  // `fetch` is an inline closure; the caller lists what it closes over in `deps`.
  const fetchRef = React.useRef(fetch);
  fetchRef.current = fetch;
  // The query key of the last run: only a reload of the SAME key keeps the old data.
  const keyRef = React.useRef<readonly unknown[] | null>(null);

  React.useEffect(() => {
    const key = [client, agencyId, ...deps];
    const same = sameKey(keyRef.current, key);
    keyRef.current = key;
    if (!agencyId) {
      setState({ status: 'loading' });
      return;
    }
    let active = true;
    setState((prev) => (same && prev.status === 'ready' ? { ...prev, refreshing: true } : { status: 'loading' }));
    void fetchRef.current(client, agencyId).then(
      (res) => {
        if (!active) return;
        if ('error' in res) {
          console.warn('useLiveQuery: primary read failed', res.error);
          setState({ status: 'error' });
        } else {
          setState({ status: 'ready', data: res.data, refreshing: false });
        }
      },
      (err: unknown) => {
        if (!active) return;
        console.warn('useLiveQuery: read threw', err);
        setState({ status: 'error' });
      },
    );
    return () => {
      active = false;
    };
  }, [client, agencyId, nonce, ...deps]);

  const refreshing = state.status === 'ready' ? state.refreshing : false;
  return { ...state, refreshing, reload } as LiveState<T>;
}

export interface Mutation {
  busy: boolean;
  /** The translated message of the last failure, or null. */
  error: string | null;
  /** The form field the last failure names (a 23505 MC, a 22023 `<field> ...`), or null. */
  field: string | null;
  /** Run one mutation at a time; resolves to its result (the error already translated in `error`). */
  run: <T>(fn: () => Promise<ActionResult<T>>) => Promise<ActionResult<T>>;
  clear: () => void;
}

export function useMutation(): Mutation {
  const message = useRpcErrorMessage();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [field, setField] = React.useState<string | null>(null);
  const busyRef = React.useRef(false);

  const clear = React.useCallback(() => {
    setError(null);
    setField(null);
  }, []);

  const run = React.useCallback(
    async <T,>(fn: () => Promise<ActionResult<T>>): Promise<ActionResult<T>> => {
      if (busyRef.current) return { data: null, error: { code: 'busy', message: 'another action is running' } };
      busyRef.current = true;
      setBusy(true);
      setError(null);
      setField(null);
      try {
        const res = await fn();
        if (res.error) {
          setError(message(res.error));
          setField(describeRpcError(res.error).field ?? null);
        }
        return res;
      } catch (err) {
        const e: RpcError = { code: undefined, message: err instanceof Error ? err.message : String(err) };
        setError(message(e));
        return { data: null, error: e };
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [message],
  );

  return { busy, error, field, run, clear };
}

/** One RPC call as an ActionResult (the error narrowed to RpcError, never thrown). */
export async function callRpc<T>(
  client: SupabaseClient,
  name: string,
  args: Record<string, unknown>,
): Promise<ActionResult<T>> {
  const { data, error } = await client.rpc(name, args);
  if (error) return { data: null, error: asRpcError(error) };
  return { data: (data ?? null) as T | null, error: null };
}

/** The current agency id, or null before the AgencyProvider is ready. */
export function useAgencyId(): string | null {
  const agency = useAgency();
  return agency.status === 'ready' ? agency.agency.id : null;
}

const NO_AGENCY: RpcError = { code: undefined, message: 'no agency selected' };

/**
 * The mutation half of a data hook: `call(name, args)` runs the RPC and, on success only, calls
 * `onSuccess` (the hook's reload). `agencyId` is the current agency (null before it is ready: the
 * call then fails without reaching the server).
 */
export function useRpcAction(onSuccess: () => void): {
  agencyId: string | null;
  call: <T>(name: string, args: Record<string, unknown>) => Promise<ActionResult<T>>;
} {
  const { client } = useAuth();
  const agencyId = useAgencyId();
  const call = React.useCallback(
    async <T,>(name: string, args: Record<string, unknown>): Promise<ActionResult<T>> => {
      const res = await callRpc<T>(client, name, args);
      if (!res.error) onSuccess();
      return res;
    },
    [client, onSuccess],
  );
  return { agencyId, call };
}

/** A call that needs the agency id but has none yet. */
export function noAgency<T>(): Promise<ActionResult<T>> {
  return Promise.resolve({ data: null, error: NO_AGENCY });
}

/**
 * Rows of a secondary read: on error, a console.warn and an empty list (the screen still renders its
 * primary rows; a count shows 0 rather than the whole page failing).
 */
export function secondary<R>(label: string, res: { data: unknown; error: unknown }): R[] {
  if (res.error) {
    console.warn(`secondary read failed: ${label}`, res.error);
    return [];
  }
  return (res.data ?? []) as R[];
}
